import { KLineData } from "klinecharts";
import { klineLineStyle, lineStyleToKLine } from "./lineStyle";
import { readStyle } from "./indicatorUtils";

export const createRVI = (settings: any) => {
  const values = settings?.values ?? {};
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const period = (values.Period as number) ?? 10;
  const rviS = readStyle(styles, "rvi", "#2196F3");
  const signalS = readStyle(styles, "signal", "#FF9600", "dashed");

  return {
    name: "rvi",
    shortName: `RVI${period}`,
    calcParams: [period],
    series: "normal" as const,
    figures: [
      { key: "rvi", title: `RVI${period}: `, type: "line" },
      { key: "signal", title: "Signal: ", type: "line" },
    ],
    styles: {
      lines: [
        klineLineStyle(rviS.color, 2, lineStyleToKLine(rviS.style)),
        klineLineStyle(signalS.color, 2, lineStyleToKLine(signalS.style)),
      ],
    },
    regenerateFigures: ([p]: number[]) => [
      { key: "rvi", title: `RVI${p}: `, type: "line" },
      { key: "signal", title: "Signal: ", type: "line" },
    ],
    calc: (dataList: KLineData[], indicator: { calcParams?: unknown[] }) => {
      const [p] = (indicator.calcParams ?? [period]) as [number];
      const n = dataList.length;
      const results: Array<{ rvi?: number; signal?: number }> = new Array(n);

      const numBuf: number[] = new Array(p);
      const denBuf: number[] = new Array(p);
      let bufIdx = 0;
      let bufCount = 0;
      let numSum = 0;
      let denSum = 0;

      const rviHistory: number[] = [];

      for (let i = 0; i < n; i++) {
        if (i < 3) {
          results[i] = {};
          continue;
        }

        const num =
          (dataList[i]!.close - dataList[i]!.open +
            2 * (dataList[i - 1]!.close - dataList[i - 1]!.open) +
            2 * (dataList[i - 2]!.close - dataList[i - 2]!.open) +
            (dataList[i - 3]!.close - dataList[i - 3]!.open)) /
          6;
        const den =
          (dataList[i]!.high - dataList[i]!.low +
            2 * (dataList[i - 1]!.high - dataList[i - 1]!.low) +
            2 * (dataList[i - 2]!.high - dataList[i - 2]!.low) +
            (dataList[i - 3]!.high - dataList[i - 3]!.low)) /
          6;

        const evN = bufCount === p ? numBuf[bufIdx]! : 0;
        const evD = bufCount === p ? denBuf[bufIdx]! : 0;
        numBuf[bufIdx] = num;
        denBuf[bufIdx] = den;
        bufIdx = (bufIdx + 1) % p;
        if (bufCount < p) bufCount++;
        numSum += num - evN;
        denSum += den - evD;

        if (bufCount < p) {
          results[i] = {};
          continue;
        }

        const rvi = denSum !== 0 ? numSum / denSum : 0;
        rviHistory.push(rvi);
        results[i] = { rvi };

        const len = rviHistory.length;
        if (len >= 4) {
          results[i]!.signal =
            (rviHistory[len - 1]! +
              2 * rviHistory[len - 2]! +
              2 * rviHistory[len - 3]! +
              rviHistory[len - 4]!) /
            6;
        }
      }

      return results;
    },
  };
};
